import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

const typographyVariants = cva(
    "transition-colors duration-200",
    {
        variants: {
            variant: {
                h1: "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
                h2: "scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0",
                h3: "scroll-m-20 text-2xl font-semibold tracking-tight",
                h4: "scroll-m-20 text-xl font-semibold tracking-tight",
                p: "leading-7 [&:not(:first-child)]:mt-6",
                "article-title": "text-[1.15rem] sm:text-[1.45rem] font-medium text-[#202124] leading-[1.4rem] sm:leading-[2.1rem] tracking-tight",
                "hero-title": "text-2xl md:text-3xl lg:text-4xl tracking-[-0.01em] leading-tight",
                "hero-subtitle": "text-lg md:text-xl text-[#5f6368] leading-relaxed max-w-4xl",
            },
            color: {
                default: "text-foreground",
                charcoal: "text-[#202124]",
                muted: "text-muted-foreground",
                primary: "text-primary",
                white: "text-white",
            },
            weight: {
                light: "font-light",
                normal: "font-normal",
                medium: "font-medium",
                semibold: "font-semibold",
                bold: "font-bold",
            }
        },
        defaultVariants: {
            variant: "p",
            color: "default",
            weight: "normal",
        },
    }
)

export type TypographyProps = {
    className?: string
    variant?: VariantProps<typeof typographyVariants>["variant"]
    color?: VariantProps<typeof typographyVariants>["color"]
    weight?: VariantProps<typeof typographyVariants>["weight"]
    as?: React.ElementType
    children?: React.ReactNode
} & React.HTMLAttributes<HTMLElement>

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
    ({ className, variant, color, weight, as, ...props }, ref) => {
        const Comp = (as ||
            (variant === "article-title" ? "h3" :
                variant === "h1" ? "h1" :
                    variant === "h2" ? "h2" :
                        variant === "h3" ? "h3" :
                            variant === "h4" ? "h4" : "p")) as any

        return (
            <Comp
                className={cn(typographyVariants({ variant, color, weight }), className)}
                ref={ref}
                {...props}
            />
        )
    }
)
Typography.displayName = "Typography"

export { Typography, typographyVariants }
